import { Alert } from '@warp-ds/react';
import cn from 'classnames';
import { SpinnerIcon } from 'shared/icons/SpinnerIcon';
import type { Money } from 'shared/util/money';
import type { PriceCleaningChoice, PrivateItemPrice } from '@/domain';
import { usePrivateItemPrice } from '@/hooks/usePrivateItemPrice';
import { PriceDetail } from './components/PriceDetail';
import { ServiceFeeDetail } from './components/ServiceFeeDetail';
import { useSyncPriceToParent } from './hooks/useSyncPriceToParent';
import { getPriceCalculations } from './utils/getPriceCalculations';

export interface PriceOverviewProps {
    startDate: Date;
    endDate: Date;
    adId?: number;
    privateItemPrice?: PrivateItemPrice;
    numberOfGuests?: number;
    cleaningChoice?: PriceCleaningChoice;
    setPrice?: (priceTotal: Money) => void;
    showConfirmedCleaning?: boolean;
    showPriceHeading?: boolean;
    className?: string;
}

export const PriceOverview = ({
    startDate,
    endDate,
    adId,
    privateItemPrice,
    numberOfGuests,
    cleaningChoice = null,
    setPrice,
    showConfirmedCleaning = false,
    showPriceHeading = true,
    className,
}: PriceOverviewProps) => {
    const shouldFetch = !privateItemPrice && adId;
    const fetchedPrice = usePrivateItemPrice(shouldFetch ? adId.toString() : '', startDate, endDate, numberOfGuests || 1, cleaningChoice);

    const priceData = privateItemPrice ?? fetchedPrice.privateItemPrice;
    const error = privateItemPrice ? undefined : fetchedPrice.error;
    const isLoading = privateItemPrice ? false : fetchedPrice.isLoading;

    useSyncPriceToParent(priceData, setPrice);

    if (isLoading && !priceData) return <SpinnerIcon />;

    if (error)
        return (
            <Alert type="negative" show>
                Beklager, noe gikk galt ved henting av pris.
            </Alert>
        );
    if (!priceData)
        return (
            <Alert type="info" show>
                Ta kontakt med utleier for pris.
            </Alert>
        );

    const { numberOfNights, nightsLabel, cleaningDisplay, totalPrice, rentalPriceWithDiscount } = getPriceCalculations(
        priceData,
        startDate,
        endDate,
        cleaningChoice,
        showConfirmedCleaning,
    );

    const showCleaningFee = cleaningDisplay?.type === 'fee';

    return (
        <div className={cn(className, 'text-14')}>
            {showPriceHeading && <strong className="text-14">Pris</strong>}
            <dl>
                <PriceDetail title={`Pris for ${numberOfNights} ${nightsLabel}`} money={rentalPriceWithDiscount} />
                {showCleaningFee && <PriceDetail title="Rengjøring" money={priceData.cleaning.amount} />}
                {!showCleaningFee && cleaningDisplay && <PriceDetail title="Rengjøring" value={cleaningDisplay.value} />}
                {priceData.serviceFee && <ServiceFeeDetail money={priceData.serviceFee.total.total} />}
                <PriceDetail title="Totalpris" money={totalPrice} isTotal />
            </dl>
        </div>
    );
};
